import React, { useRef, useState } from "react";
import styled from "styled-components";
import { Form, FORM_FIELD_TYPES, IFormItems } from "site/ui";
import { AppButton } from "site/ui";
import { FormInstance } from "antd";
import usePaymentStore from "store/paymentStore";

interface ErrorState {
  firstName?: string;
  lastName?: string;
  cardNumber?: string;
  expiry?: string;
  cvv?: string;
  country?: string;
  city?: string;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  font-family: "Manrope", sans-serif;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 10px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
`;

const ButtonContainer = styled.div`
  padding: 20px;
  border-top: 1px solid #eee;
`;

export interface AddPaymentModalProps{
  open: boolean;
  close: () => void;
}
const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
  open, close
}) => {
  const {
    paymentMethod,
    addPaymentMethod,
    resetPaymentForm,
  } = usePaymentStore();

  const [errors, setErrors] = useState<ErrorState>({});
  const formRef = useRef<FormInstance>(null);

  if (!open) return null;

  const validateForm = (): boolean => {
    const newErrors: ErrorState = {};
    const values = formRef.current?.getFieldsValue();

    if (!values.firstName) newErrors.firstName = "First name is required";
    if (!values.lastName) newErrors.lastName = "Last name is required";

    if (!values.cardNumber) {
      newErrors.cardNumber = "Card number is required";
    } else if (!/^\d{16}$/.test(values.cardNumber.replace(/\s/g, ""))) {
      newErrors.cardNumber = "Invalid card number";
    }

    if (!values.expiryMonth || !values.expiryYear) {
      newErrors.expiry = "Expiry date is required";
    }

    if (!values.cvv) {
      newErrors.cvv = "CVV is required";
    } else if (!/^\d{3,4}$/.test(values.cvv)) {
      newErrors.cvv = "Invalid CVV";
    }

    if (!values.country) newErrors.country = "Country is required";
    if (!values.city) newErrors.city = "City is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (): void => {
    if (formRef.current) {
      formRef.current
        .validateFields()
        .then((values) => {
          if (validateForm()) {
            addPaymentMethod({ ...values });

            resetPaymentForm();
            close();
          }
        })
        .catch((info) => {
          console.log("Validate Failed:", info);
        });
    }
  };

  const formatCardNumber = (value: string): string => {
    const digits = value.replace(/\D/g, "");

    let formatted = "";
    for (let i = 0; i < digits.length; i++) {
      if (i > 0 && i % 4 === 0) {
        formatted += " ";
      }
      formatted += digits[i];
    }

    return formatted.substring(0, 19);
  };

  const monthOptions = [];
  for (let i = 1; i <= 12; i++) {
    const value = i.toString().padStart(2, "0");
    monthOptions.push({
      label: value,
      value: value,
    });
  }

  const yearOptions = [];
  const currentYear = new Date().getFullYear();
  for (let i = 0; i < 10; i++) {
    const year = (currentYear + i).toString();
    yearOptions.push({
      label: year,
      value: year,
    });
  }

  const countryOptions = [
    { label: "Nigeria", value: "Nigeria" },
    { label: "Ghana", value: "Ghana" },
    { label: "Kenya", value: "Kenya" },
    { label: "South Africa", value: "South Africa" },
    { label: "United States", value: "United States" },
    { label: "United Kingdom", value: "United Kingdom" },
  ];

  const formItems: IFormItems[] = [
    {
      fieldType: FORM_FIELD_TYPES.FIELDS,
      itemProps: {
        name: "name-row",
      },
      fieldProps: [
        {
          fieldType: FORM_FIELD_TYPES.TEXT,
          colProps: { span: 12 },
          itemProps: {
            label: "First name",
            name: "firstName",
            initialValue: paymentMethod.firstName,
            rules: [{ required: true, message: "First name is required" }],
          },
          fieldProps: {
            placeholder: "Enter first name",
          },
        },
        {
          fieldType: FORM_FIELD_TYPES.TEXT,
          colProps: { span: 12 },
          itemProps: {
            label: "Last name",
            name: "lastName",
            initialValue: paymentMethod.lastName,
            rules: [{ required: true, message: "Last name is required" }],
          },
          fieldProps: {
            placeholder: "Enter last name",
          },
        },
      ],
    },
    {
      fieldType: FORM_FIELD_TYPES.TEXT,
      itemProps: {
        label: "Card number",
        name: "cardNumber",
        initialValue: paymentMethod.cardNumber,
        rules: [{ required: true, message: "Card number is required" }],
      },
      fieldProps: {
        placeholder: "1234 5678 9101 1213",
        maxLength: 19,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          const formatted = formatCardNumber(e.target.value);
          formRef.current?.setFieldsValue({ cardNumber: formatted });
        },
      },
    },
    {
      fieldType: FORM_FIELD_TYPES.FIELDS,
      itemProps: {
        name: "expiry-cvv-row",
      },
      fieldProps: [
        {
          fieldType: FORM_FIELD_TYPES.FIELDS,
          colProps: { span: 12 },
          itemProps: {
            label: "Expiry date",
            name: "expiry-container",
          },
          fieldProps: [
            {
              fieldType: FORM_FIELD_TYPES.SELECT,
              colProps: { span: 12 },
              itemProps: {
                name: "expiryMonth",
                initialValue: paymentMethod.expiryMonth,
                rules: [{ required: true, message: "Month is required" }],
                noStyle: true,
              },
              fieldProps: {
                placeholder: "MM",
                options: monthOptions,
                style: { width: "100%" },
              },
            },
            {
              fieldType: FORM_FIELD_TYPES.SELECT,
              colProps: { span: 12 },
              itemProps: {
                name: "expiryYear",
                initialValue: paymentMethod.expiryYear,
                rules: [{ required: true, message: "Year is required" }],
                noStyle: true,
              },
              fieldProps: {
                placeholder: "YYYY",
                options: yearOptions,
                style: { width: "100%" },
              },
            },
          ],
        },
        {
          fieldType: FORM_FIELD_TYPES.TEXT,
          colProps: { span: 12 },
          itemProps: {
            label: "CVV",
            name: "cvv",
            initialValue: paymentMethod.cvv,
            rules: [{ required: true, message: "CVV is required" }],
          },
          fieldProps: {
            placeholder: "123",
            maxLength: 4,
          },
        },
      ],
    },
    {
      fieldType: FORM_FIELD_TYPES.SELECT,
      itemProps: {
        label: "Country",
        name: "country",
        initialValue: paymentMethod.country,
        rules: [{ required: true, message: "Country is required" }],
      },
      fieldProps: {
        placeholder: "Select a country",
        options: countryOptions,
      },
    },
    {
      fieldType: FORM_FIELD_TYPES.TEXT,
      itemProps: {
        label: "City",
        name: "city",
        initialValue: paymentMethod.city,
        rules: [{ required: true, message: "City is required" }],
      },
      fieldProps: {
        placeholder: "Enter city",
      },
    },
  ];

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Add a payment method</ModalTitle>
          <CloseButton onClick={close}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          <Form
            formRef={formRef}
            formProps={{
              layout: "vertical",
              initialValues: {
                firstName: paymentMethod.firstName,
                lastName: paymentMethod.lastName,
                cardNumber: paymentMethod.cardNumber,
                expiryMonth: paymentMethod.expiryMonth,
                expiryYear: paymentMethod.expiryYear,
                cvv: paymentMethod.cvv,
                country: paymentMethod.country,
                city: paymentMethod.city,
              },
              style: { padding: 0 },
            }}
            items={formItems}
          />
        </ModalBody>

        <ButtonContainer>
          <AppButton variant="primary" fullWidth onClick={handleSubmit}>
            Add payment method
          </AppButton>
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AddPaymentModal;
